import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { TalentfulCTA } from "./TalentfulCTA";
import { HiringChart } from "./charts/HiringChart";
import { DepartmentCard } from "./DepartmentCard";

interface CalculatorResultsProps {
  totalHiresNeeded: {
    gtm: number;
    gna: number;
    tech: number;
    exec: number;
    total: number;
  };
  monthlyHiringPace: {
    gtm: number;
    gna: number;
    tech: number;
    exec: number;
    total: number;
  };
  timelineData: Array<{
    name: string;
    gtm: number;
    gna: number;
    tech: number;
    exec: number;
    total: number;
  }>;
  recruitingGoals: {
    gtm: number;
    gna: number;
    tech: number;
    exec: number;
  };
  totalRecruitersNeeded?: number;
  ftePercentage?: number;
  outsourcedPercentage?: number;
  currentRecruiters?: {
    gtm: number;
    gna: number;
    tech: number;
    exec: number;
  };
  rampPeriodMonths?: number;
  timelineMonths?: number;
}

export const CalculatorResults = ({ 
  totalHiresNeeded,
  monthlyHiringPace,
  timelineData,
  recruitingGoals,
  totalRecruitersNeeded,
  ftePercentage,
  outsourcedPercentage,
  currentRecruiters = { gtm: 0, gna: 0, tech: 0, exec: 0 },
  rampPeriodMonths = 1,
  timelineMonths = 12,
}: CalculatorResultsProps) => {
  const [goalAchievedMonths, setGoalAchievedMonths] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    // Calculate when each department reaches its goal
    const goals: { [key: string]: number } = {};
    
    ['tech', 'gtm', 'gna', 'exec'].forEach(dept => {
      let goalAchieved = false;
      let cumulativeHires = 0;
      
      for (let i = 0; i < timelineData.length; i++) {
        const monthlyHires = Number(timelineData[i][dept as keyof typeof timelineData[0]]) || 0;
        cumulativeHires += monthlyHires;
        
        const targetValue = Number(totalHiresNeeded[dept as keyof typeof totalHiresNeeded]) || 0;
        
        if (cumulativeHires >= targetValue && !goalAchieved) {
          goals[dept] = i + 1;
          goalAchieved = true;
          console.log(`${dept} reached goal of ${targetValue} hires at month ${i + 1} with ${cumulativeHires} cumulative hires`);
        }
      }
      
      if (!goalAchieved) {
        goals[dept] = timelineMonths;
        console.log(`${dept} did not reach goal within timeline. Target: ${totalHiresNeeded[dept as keyof typeof totalHiresNeeded]}, Achieved: ${cumulativeHires}`);
      }
    });

    console.log('Goal achievement months:', goals);
    setGoalAchievedMonths(goals);
  }, [timelineData, totalHiresNeeded, timelineMonths]);

  const areas = [
    { key: "tech", label: "Tech & Product", color: "#F59E0B" },
    { key: "gtm", label: "Go-to-market", color: "#4F46E5" },
    { key: "gna", label: "G&A", color: "#10B981" },
    { key: "exec", label: "Executive", color: "#EF4444" },
  ];

  // Calculate total team capacity
  const calculateTotalTeamCapacity = () => {
    return Object.entries(currentRecruiters).reduce((total, [dept, count]) => {
      const monthlyGoal = recruitingGoals[dept as keyof typeof recruitingGoals];
      return total + (count * monthlyGoal * timelineMonths);
    }, 0);
  };

  const totalTeamCapacity = calculateTotalTeamCapacity();
  const hiringCapacityDifference = totalTeamCapacity - totalHiresNeeded.total;
  const isExcessCapacity = hiringCapacityDifference > 0;

  return (
    <Card className="p-6 space-y-12 results-section animate-fade-in">
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {areas.map((area) => (
            <DepartmentCard
              key={area.key}
              label={area.label}
              color={area.color}
              totalHires={totalHiresNeeded[area.key as keyof typeof totalHiresNeeded]}
              currentRecruiters={currentRecruiters[area.key as keyof typeof currentRecruiters]}
              monthlyGoal={recruitingGoals[area.key as keyof typeof recruitingGoals]}
              timelineMonths={timelineMonths}
              rampPeriodMonths={rampPeriodMonths}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 border border-gray-200">
            <h3 className="font-semibold text-lg mb-3">Total Hiring Goal</h3>
            <div className={`text-3xl font-bold ${totalHiresNeeded.total > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {totalHiresNeeded.total}
            </div>
            <p className="text-sm text-gray-500 mt-1">Total hires needed across all departments</p>
          </Card>

          <Card className="p-4 border border-gray-200">
            <h3 className="font-semibold text-lg mb-3">Current Hiring Capacity</h3>
            <div className={`text-3xl font-bold ${totalTeamCapacity > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {totalTeamCapacity}
            </div>
            <p className="text-sm text-gray-500 mt-1">Maximum hires possible with current team</p>
          </Card>

          <Card className="p-4 border border-gray-200">
            <h3 className="font-semibold text-lg mb-3">
              {isExcessCapacity ? "Excess Hiring Capacity" : "Needed Hiring Capacity"}
            </h3>
            <div className={`text-3xl font-bold ${isExcessCapacity ? 'text-green-500' : 'text-red-500'}`}>
              {Math.abs(hiringCapacityDifference)}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {isExcessCapacity ? "Additional hires possible" : "Additional hiring capacity needed"}
            </p>
          </Card>
        </div>
      </div>

      <div className="mt-16 mb-16">
        <HiringChart 
          timelineData={timelineData}
          goalAchievedMonths={goalAchievedMonths}
          timelineMonths={timelineMonths}
          totalHiresNeeded={totalHiresNeeded}
        />
      </div>

      {totalRecruitersNeeded !== undefined && (
        <div className="mt-16">
          <TalentfulCTA
            totalRecruitersNeeded={totalRecruitersNeeded}
            ftePercentage={ftePercentage || 0}
            outsourcedPercentage={outsourcedPercentage || 0}
            currentRecruiters={Object.values(currentRecruiters).reduce((sum, val) => sum + val, 0)}
            rampPeriodMonths={rampPeriodMonths || 0}
            timelineMonths={timelineMonths || 0}
            monthlyHiringGoals={recruitingGoals}
          />
        </div>
      )}
    </Card>
  );
};